import type { Tenant, TenantSuperadmin } from '@t4s/api-client/model'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { brandColourState, COLOUR_BADGE } from '../branding/brand-colour.ts'
import type { fetchTenantBranding } from '../branding/gateway.ts'
import {
  CATALOGUE_QUERY_KEY,
  fetchFeatureFlagCatalogue,
  fetchTenantFlags,
  tenantFlagsQueryKey,
} from '../flags/gateway.ts'
import { codeOf } from '../lib/api-error.ts'
import { messageFor } from '../lib/messages.ts'
import { Card, CardError, CardSkeleton } from './card.tsx'
import { CopyButton } from './copy-button.tsx'
import { formatInstantWithoutZone, ownerTimeZone } from './instants.ts'
import { LOCALE_LABELS } from './locales.ts'

/**
 * The summary row and the four cards of the Overview that only describe — Identity,
 * Configuration, Branding and Feature flags. docs/ux/PLATFORM-ADMIN.md §11.1.
 *
 * Every one of them takes the tenant read as `Tenant | undefined`: `undefined` is "still on
 * its way", drawn as the card's own skeleton under its real heading (§11.4, §18.1). A failed
 * tenant read never reaches here — the page replaces itself with the failure notice first.
 *
 * What the owner reads here about **someone else's** records (a superadmin's sign-in) is in
 * the access card; this file is the tenant's own configuration and the platform's records of
 * it, so its instants are the **owner's** (§7.5).
 */

/** Language names, default first — the same shape the tenant list shows. */
function languageNames(tenant: Tenant): string {
  return tenant.enabledLocales.map((locale) => LOCALE_LABELS[locale]).join(', ')
}

type SuperadminRead = {
  readonly data: readonly TenantSuperadmin[] | undefined
  readonly isError: boolean
}

/** The word under "Can anyone sign in?" — the reason, so the answer is never bare. */
function signInNote(tenant: Tenant): string {
  if (tenant.state === 'retired') {
    return 'this tenant is retired'
  }
  if (!tenant.canSignIn) {
    return 'no account is active'
  }
  return tenant.users.activeSuperadmins > 0 ? 'an active superadmin exists' : 'no active superadmin'
}

function superadminNote(tenant: Tenant, read: SuperadminRead): string {
  if (read.data === undefined) {
    return read.isError
      ? `${tenant.users.activeSuperadmins} active · the list could not be read, see Access`
      : `${tenant.users.activeSuperadmins} active`
  }
  const count = (status: TenantSuperadmin['status']) =>
    read.data?.filter((user) => user.status === status).length ?? 0
  const disabled = count('disabled')
  return `${count('active')} active · ${count('pending')} invited${disabled > 0 ? ` · ${disabled} disabled` : ''}`
}

export function SummaryRow({
  tenant,
  superadmins,
}: {
  readonly tenant: Tenant | undefined
  readonly superadmins: SuperadminRead
}) {
  if (tenant === undefined) {
    return (
      <dl className="stats" aria-busy="true">
        {['Accounts', 'Signed in now', 'Superadmins', 'Can anyone sign in?'].map((label) => (
          <div key={label} className="card stat">
            <dt className="stat-label">{label}</dt>
            <dd className="stat-value" aria-hidden="true">
              <span className="skeleton skeleton-narrow" />
            </dd>
          </div>
        ))}
      </dl>
    )
  }

  const { users } = tenant

  return (
    <dl className="stats">
      <div className="card stat">
        <dt className="stat-label">Accounts</dt>
        <dd className="stat-value num">{users.total}</dd>
        <dd className="stat-note">
          {users.active} active · {users.pending} pending · {users.disabled} disabled
        </dd>
      </div>
      <div className="card stat">
        <dt className="stat-label">Signed in now</dt>
        <dd className="stat-value num">{tenant.liveSessions}</dd>
        <dd className="stat-note">live sessions</dd>
      </div>
      <div className="card stat">
        <dt className="stat-label">Superadmins</dt>
        <dd className="stat-value num">
          {superadmins.data !== undefined ? (
            superadmins.data.length
          ) : superadmins.isError ? (
            '—'
          ) : (
            <span className="skeleton skeleton-narrow" aria-hidden="true" />
          )}
        </dd>
        <dd className="stat-note">{superadminNote(tenant, superadmins)}</dd>
      </div>
      <div className="card stat">
        <dt className="stat-label">Can anyone sign in?</dt>
        <dd className={tenant.canSignIn ? 'stat-value stat-yes' : 'stat-value stat-no'}>
          {tenant.canSignIn ? 'Yes' : 'No'}
        </dd>
        <dd className="stat-note">{signInNote(tenant)}</dd>
      </div>
    </dl>
  )
}

export function IdentityCard({ tenant }: { readonly tenant: Tenant | undefined }) {
  const zone = ownerTimeZone()

  return (
    <Card title="Identity" busy={tenant === undefined}>
      {tenant === undefined ? (
        <CardSkeleton lines={4} />
      ) : (
        <div className="card-body">
          <dl className="kv">
            <dt>Slug</dt>
            <dd>
              <code>{tenant.slug}</code> <span className="muted">— fixed forever</span>
            </dd>
            <dt>Tenant id</dt>
            <dd className="row row-tight">
              <code>{tenant.tenantId}</code>
              <CopyButton value={tenant.tenantId} noun="tenant id" quiet />
            </dd>
            <dt>State</dt>
            <dd>
              <span className={`badge badge-${tenant.state}`}>
                {tenant.state === 'retired' ? 'Retired' : 'Live'}
              </span>
              {tenant.state === 'retired' && tenant.retiredAt !== null ? (
                <span className="muted">
                  {' '}
                  {formatInstantWithoutZone(tenant.retiredAt, zone)} <code>{zone}</code>
                </span>
              ) : null}
            </dd>
            <dt>Created</dt>
            <dd>
              {formatInstantWithoutZone(tenant.createdAt, zone)}{' '}
              <code className="muted">{zone}</code>
            </dd>
          </dl>
        </div>
      )}
    </Card>
  )
}

export function ConfigurationCard({
  tenant,
  tenantId,
}: {
  readonly tenant: Tenant | undefined
  readonly tenantId: string
}) {
  const retired = tenant?.state === 'retired'

  return (
    <Card
      title="Configuration"
      busy={tenant === undefined}
      action={
        tenant === undefined || retired ? undefined : (
          <Link to="/tenants/$tenantId/settings" params={{ tenantId }} className="button button-sm">
            Edit settings
          </Link>
        )
      }
    >
      {tenant === undefined ? (
        <CardSkeleton lines={3} />
      ) : (
        <div className="card-body">
          <dl className="kv">
            <dt>Timezone</dt>
            <dd>
              <code>{tenant.timeZone}</code>
            </dd>
            <dt>Default language</dt>
            <dd>{LOCALE_LABELS[tenant.defaultLocale]}</dd>
            <dt>Available</dt>
            <dd>{languageNames(tenant)}</dd>
          </dl>
          {retired ? (
            <p className="muted small">
              A retired tenant is not configured. Its settings are read-only.
            </p>
          ) : null}
        </div>
      )}
    </Card>
  )
}

/** The branding read both this card and the build key card show, as the page holds it. */
export type BrandingRead = {
  readonly data: Awaited<ReturnType<typeof fetchTenantBranding>> | undefined
  readonly isPending: boolean
  readonly isError: boolean
  readonly error: unknown
  /** When `data` was last written, in ms — the cache's own clock, compared with a rotation's. */
  readonly dataUpdatedAt: number
  readonly refetch: () => void
}

export function BrandingCard({
  tenantId,
  branding,
}: {
  readonly tenantId: string
  readonly branding: BrandingRead
}) {
  const config = branding.data
  const colour = config === undefined ? undefined : brandColourState(config.colors.brand)

  return (
    <Card
      title="Branding"
      busy={branding.isPending}
      action={
        <Link to="/tenants/$tenantId/branding" params={{ tenantId }} className="button button-sm">
          Open branding
        </Link>
      }
    >
      {branding.isPending ? (
        <CardSkeleton lines={3} />
      ) : config === undefined || colour === undefined ? (
        <CardError
          message={messageFor('branding', codeOf(branding.error))}
          onRetry={branding.refetch}
        />
      ) : (
        <div className="card-body">
          <div className="row">
            <span
              className="swatch"
              // The tenant's own colour, computed by `buildColors` — never a role of this
              // stylesheet, and not a pairing it asserts.
              style={{ backgroundColor: colour.swatch }}
              aria-hidden="true"
            />
            <div className="stack-tight">
              <span>
                <code className="wrap">{config.colors.brand}</code>{' '}
                <span className={COLOUR_BADGE[colour.state].className}>
                  {COLOUR_BADGE[colour.state].label}
                </span>
              </span>
              <span className="tiny muted">{COLOUR_BADGE[colour.state].meaning}</span>
            </div>
          </div>
          <dl className="kv">
            <dt>Logo</dt>
            <dd>
              {config.logoUrl === null ? (
                <span className="muted">Not set</span>
              ) : (
                // Text, never an image or a link: it is a URL the tenant's side supplied.
                <code className="wrap small">{config.logoUrl}</code>
              )}
            </dd>
            <dt>App name</dt>
            <dd>
              {config.appName === '' ? <span className="muted">Not set</span> : config.appName}
            </dd>
          </dl>
        </div>
      )}
    </Card>
  )
}

export function FlagsCard({ tenantId }: { readonly tenantId: string }) {
  const catalogue = useQuery({
    queryKey: CATALOGUE_QUERY_KEY,
    queryFn: fetchFeatureFlagCatalogue,
  })
  const hasCatalogue = catalogue.isSuccess && catalogue.data.length > 0
  const flags = useQuery({
    queryKey: tenantFlagsQueryKey(tenantId),
    queryFn: () => fetchTenantFlags(tenantId),
    // With nothing in the catalogue there is nothing to label, so the tenant's own map is not
    // worth a request — and its failure would be an error about data this card never shows.
    enabled: hasCatalogue,
  })

  const link = (
    <Link to="/tenants/$tenantId/flags" params={{ tenantId }} className="button button-sm">
      Open feature flags
    </Link>
  )

  if (catalogue.isError || flags.isError) {
    const failed = catalogue.isError ? catalogue : flags
    return (
      <Card title="Feature flags" action={link}>
        <CardError
          message={messageFor('flags', codeOf(failed.error))}
          onRetry={() => {
            void (catalogue.isError ? catalogue.refetch() : flags.refetch())
          }}
        />
      </Card>
    )
  }

  if (catalogue.isPending || (hasCatalogue && flags.isPending)) {
    return (
      <Card title="Feature flags" action={link} busy>
        <CardSkeleton lines={2} />
      </Card>
    )
  }

  const on = (catalogue.data ?? []).filter((entry) => flags.data?.[entry.key] === true)

  return (
    <Card title="Feature flags" action={link}>
      <div className="card-body">
        {!hasCatalogue ? (
          <p className="small secondary">
            This platform defines no flags yet, so there is nothing to switch for this tenant.
          </p>
        ) : on.length === 0 ? (
          <p className="small secondary">No flags are switched on for this tenant.</p>
        ) : (
          <>
            <p className="small secondary">Switched on for this tenant:</p>
            <ul className="flag-list">
              {on.map((entry) => (
                <li key={entry.key}>{entry.label}</li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Card>
  )
}
